import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Label } from 'recharts';

const MIN_SESSIONS = 4;

const shortDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

export default function StrengthChart({ series = [], title = 'Pull-up Strength' }) {
  if (series.length < MIN_SESSIONS) {
    const remaining = MIN_SESSIONS - series.length;
    return (
      <div className="border border-mono-line bg-mono-card p-8 mb-6" data-testid="free-strength-locked">
        <p className="text-mono-mute font-body">
          Your strength chart unlocks at {MIN_SESSIONS} logged sessions. {remaining} to go.
        </p>
      </div>
    );
  }

  const data = series.map((point) => ({ label: shortDate(point.date), one_rm: point.one_rm }));
  const values = data.map((d) => d.one_rm);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const first = values[0];
  const latest = values[values.length - 1];
  const gained = latest > first;
  const diff = (latest - first).toFixed(1);

  return (
    <div className="border border-mono-line bg-mono-card p-6 mb-6" data-testid="free-strength-chart-card">
      <h2 className="font-heading text-xs font-bold uppercase tracking-[0.2em] text-mono-mute mb-3">
        {title}
      </h2>

      <p
        className={`font-heading text-3xl sm:text-4xl font-black tracking-tight mb-5 ${gained ? 'text-crimson' : 'text-white'}`}
        data-testid="free-strength-headline"
      >
        {gained ? `+${diff}kg added weight since you started` : `Estimated 1RM: +${latest.toFixed(1)}kg added weight`}
      </p>

      <div className="h-[280px]" data-testid="free-strength-chart">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
            <CartesianGrid stroke="#2A2A2A" strokeDasharray="3 3" />
            <XAxis
              dataKey="label"
              stroke="#2A2A2A"
              tick={{ fill: '#8E8E8E', fontSize: 12, fontFamily: 'Manrope' }}
              tickMargin={8}
            />
            <YAxis
              domain={[min - 2, max + 2]}
              stroke="#2A2A2A"
              tick={{ fill: '#8E8E8E', fontSize: 12, fontFamily: 'Manrope' }}
              tickFormatter={(v) => Math.round(v * 10) / 10}
              width={72}
            >
              <Label
                value="Estimated 1RM — added weight (kg)"
                angle={-90}
                position="insideLeft"
                style={{ fill: '#8E8E8E', fontSize: 12, fontFamily: 'Manrope', textAnchor: 'middle' }}
              />
            </YAxis>
            <Tooltip
              contentStyle={{
                background: '#141414',
                border: '1px solid #2A2A2A',
                borderRadius: 0,
                fontFamily: 'Manrope',
                fontSize: 12,
              }}
              labelStyle={{ color: '#8E8E8E' }}
              itemStyle={{ color: '#FFFFFF' }}
              formatter={(v) => [`+${v}kg added weight`, 'Estimated 1RM']}
            />
            <Line
              type="linear"
              dataKey="one_rm"
              stroke="#C8322C"
              strokeWidth={2}
              dot={{ fill: '#C8322C', stroke: '#C8322C', r: 4 }}
              activeDot={{ fill: '#C8322C', stroke: '#FFFFFF', strokeWidth: 1, r: 5 }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <p className="text-mono-mute font-body text-xs mt-4" data-testid="free-strength-footnote">
        Your best estimate so far, from your logged reps. Not a tested max.
      </p>
    </div>
  );
}
